<template>
  <Modal :config="modal">
    <template v-slot:header>
      <h4 class="title is-4">{{ translations.famoco_settings }}</h4>
    </template>

    <!-- ── Step 1: token entry ──────────────────────────────────── -->
    <template v-if="step === 'token'">
      <div class="field">
        <label class="label">{{ translations.api_token }}</label>
        <div class="control">
          <input
            class="input"
            type="password"
            autocomplete="new-password"
            :placeholder="translations.api_token_placeholder"
            v-model="tokenInput"
            @keyup.enter="validateToken()"
          />
        </div>
        <p v-if="famocoState.config && famocoState.config.has_api_token" class="help has-text-success">
          {{ translations.token_is_set }} — {{ translations.api_token_placeholder_update }}
        </p>
      </div>

      <div v-if="validationError" class="notification is-danger is-light">
        {{ validationError }}
      </div>

      <div class="buttons mt-4">
        <button
          class="button is-primary"
          :class="{ 'is-loading': validating }"
          :disabled="!tokenInput"
          @click="validateToken()"
        >
          {{ translations.validate_token }}
        </button>
        <button v-if="famocoState.config" class="button" @click="cancelTokenUpdate()">
          {{ translations.cancel }}
        </button>
      </div>
    </template>

    <!-- ── Step 2: org selection ────────────────────────────────── -->
    <template v-else-if="step === 'orgs'">
      <p class="has-text-success mb-4">
        <span class="icon"><i class="material-icons" style="font-size:1rem;vertical-align:middle">check_circle</i></span>
        {{ translations.token_valid }}
      </p>

      <div class="field">
        <label class="label">{{ translations.select_org }}</label>
        <div class="control">
          <div class="select is-fullwidth">
            <select v-model="selectedOrgId">
              <option v-for="org in availableOrgs" :key="org.id" :value="org.id">
                {{ org.name }} ({{ org.id }})
              </option>
            </select>
          </div>
        </div>
      </div>

      <div v-if="saveError" class="notification is-danger is-light mt-3">
        {{ saveError }}
      </div>

      <div class="buttons mt-4">
        <button
          class="button is-primary"
          :class="{ 'is-loading': saving }"
          :disabled="!selectedOrgId"
          @click="saveAndSync()"
        >
          {{ saving ? translations.saving_and_syncing : translations.save_and_sync }}
        </button>
        <button class="button" @click="backToToken()">
          {{ translations.cancel }}
        </button>
      </div>
    </template>

    <!-- ── Step 3: configured ───────────────────────────────────── -->
    <template v-else-if="step === 'configured'">
      <div class="famoco-config-summary mb-4">
        <div class="famoco-config-row">
          <span class="famoco-config-key">{{ translations.org }}</span>
          <span class="famoco-config-val">
            {{ famocoState.config.org_name || famocoState.config.org_id }}
            <span class="has-text-grey is-size-7" v-if="famocoState.config.org_name">({{ famocoState.config.org_id }})</span>
          </span>
        </div>
        <div class="famoco-config-row">
          <span class="famoco-config-key">{{ translations.has_api_token }}</span>
          <span class="has-text-success">{{ translations.token_is_set }}</span>
        </div>
        <div class="famoco-config-row" v-if="famocoState.config.updated_at">
          <span class="famoco-config-key">{{ translations.updated_at }}</span>
          <span class="famoco-config-val has-text-grey">{{ famocoState.config.updated_at | formatDate }}</span>
        </div>
      </div>

      <div v-if="syncError" class="notification is-danger is-light mb-3">
        {{ syncError }}
      </div>
      <div v-if="syncResult" class="notification mb-3" :class="syncResult.devices.failed > 0 ? 'is-warning' : 'is-success'">
        <p>{{ translations.sync_all_success }}</p>
        <p class="is-size-7 mt-1">
          {{ syncResult.fleets }} fleets · {{ syncResult.devices.synced }}/{{ syncResult.devices.total }} devices synced
          <span v-if="syncResult.devices.failed > 0"> · {{ syncResult.devices.failed }} failed</span>
        </p>
        <ul v-if="syncResult.devices.errors.length" class="mt-2">
          <li v-for="e in syncResult.devices.errors" :key="e.device_id" class="is-size-7">
            {{ e.serial_id }} ({{ e.famoco_id }}): {{ e.error }}
          </li>
        </ul>
      </div>

      <div class="buttons">
        <button
          class="button is-info"
          :class="{ 'is-loading': syncing }"
          @click="runSyncAll()"
        >
          {{ translations.sync_all }}
        </button>
        <button class="button" @click="startTokenUpdate()">
          {{ translations.update_token }}
        </button>
      </div>

      <!-- Stats -->
      <div class="famoco-stats mt-5">
        <div class="famoco-stat" v-if="famocoState.config.licenses != null">
          <span class="famoco-stat-value">{{ famocoState.config.licenses }}</span>
          <span class="famoco-stat-label">{{ translations.licenses }}</span>
        </div>
        <div class="famoco-stat">
          <span class="famoco-stat-value">{{ famocoState.fleets.length }}</span>
          <span class="famoco-stat-label">{{ translations.fleets }}</span>
        </div>
        <div class="famoco-stat">
          <span class="famoco-stat-value">{{ famocoDeviceCount }}</span>
          <span class="famoco-stat-label">{{ translations.famoco_devices }}</span>
        </div>
        <div class="famoco-stat">
          <span class="famoco-stat-value">{{ famocoInFleetCount }}</span>
          <span class="famoco-stat-label">{{ translations.in_fleet }}</span>
        </div>
        <div class="famoco-stat">
          <span class="famoco-stat-value">{{ famocoInStockCount }}</span>
          <span class="famoco-stat-label">{{ translations.in_stock }}</span>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script lang="ts">
import Modal from '@/components/modal.vue';
import { validateFamocoToken, syncFamocoAll } from '@/api/famoco';
import { IFamocoOrg, IFamocoSyncAllResult } from '@/models/famoco';
import { IFamocoState } from '@/store/modules/famoco';
import { IStateDevice } from '@/store/modules/devices';
import { IFamocoTranslations } from '@/translations';
import { Component, Vue } from 'vue-property-decorator';
import { MutationPayload } from 'vuex';
import { Action, Getter } from 'vuex-class';

import store from '@/store';
import router from '@/router';

type Step = 'token' | 'orgs' | 'configured';

@Component({
  name: 'FamocoSettings',
  components: { Modal },
  filters: {
    formatDate(value: string) {
      if (!value) { return ''; }
      return new Date(value).toLocaleString();
    }
  }
})
class FamocoSettings extends Vue {
  @Action('fetchFamocoConfig') public fetchConfig!: () => void;
  @Action('saveFamocoConfig') public saveConfig!: (payload: { api_token?: string; org_id: number; base_url: string }) => void;

  @Getter('getFamocoState') public famocoState!: IFamocoState;
  @Getter('getDevices') public devices!: IStateDevice[];
  @Getter('getFamocoTranslations') public translations!: IFamocoTranslations;

  public modal = {
    isActive: true,
    isLarge: true,
    close: () => { router.push('/devices'); }
  };

  public step: Step = 'token';
  public tokenInput = '';
  public availableOrgs: IFamocoOrg[] = [];
  public selectedOrgId: number | null = null;

  public validating = false;
  public validationError = '';
  public saving = false;
  public saveError = '';
  public syncing = false;
  public syncResult: IFamocoSyncAllResult | null = null;
  public syncError = '';

  get famocoDeviceCount() { return this.devices.filter(d => d.famoco_id).length; }
  get famocoInFleetCount() { return this.devices.filter(d => d.famoco_id && d.famoco_fleet).length; }
  get famocoInStockCount() { return this.devices.filter(d => d.famoco_id && !d.famoco_fleet).length; }

  public created() {
    this.fetchConfig();

    const unsubscribe = store.subscribe((mutation: MutationPayload) => {
      if (mutation.type === 'setFamocoConfigResponse' || mutation.type === 'setFamocoConfigSaved') {
        if (this.famocoState.config) {
          this.step = 'configured';
        }
      }
    });

    this.$once('hook:destroyed', unsubscribe);

    if (this.famocoState.config) {
      this.step = 'configured';
    }
  }

  public validateToken() {
    if (!this.tokenInput) { return; }
    this.validating = true;
    this.validationError = '';

    validateFamocoToken(this.tokenInput)
      .then(response => {
        this.availableOrgs = response.data.orgs;
        this.selectedOrgId = this.availableOrgs.length > 0 ? this.availableOrgs[0].id : null;
        this.step = 'orgs';
      })
      .catch(err => {
        this.validationError = err.response?.data?.detail || this.translations.validation_error;
      })
      .finally(() => {
        this.validating = false;
      });
  }

  public saveAndSync() {
    if (!this.selectedOrgId) { return; }
    this.saving = true;
    this.saveError = '';

    this.saveConfig({ api_token: this.tokenInput, org_id: this.selectedOrgId, base_url: 'https://my.famoco.com' });

    const unsubscribe = store.subscribe((mutation: MutationPayload) => {
      if (mutation.type === 'setFamocoConfigSaved') {
        this.runSyncAll();
        this.saving = false;
        unsubscribe();
      }
      if (mutation.type === 'setFamocoError') {
        this.saving = false;
        this.saveError = this.famocoState.message;
        unsubscribe();
      }
    });
  }

  public runSyncAll() {
    this.syncing = true;
    this.syncError = '';
    this.syncResult = null;

    syncFamocoAll()
      .then(response => {
        this.syncResult = response.data;
        this.step = 'configured';
        this.fetchConfig();
      })
      .catch(err => {
        this.syncError = err.response?.data?.detail || this.translations.sync_all_error;
      })
      .finally(() => {
        this.syncing = false;
      });
  }

  public startTokenUpdate() {
    this.tokenInput = '';
    this.validationError = '';
    this.step = 'token';
  }

  public cancelTokenUpdate() {
    this.tokenInput = '';
    this.validationError = '';
    this.step = 'configured';
  }

  public backToToken() {
    this.step = 'token';
    this.availableOrgs = [];
    this.selectedOrgId = null;
  }
}

export default FamocoSettings;
</script>

<style scoped>
.famoco-config-summary {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
}
.famoco-config-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.famoco-config-row:last-child { border-bottom: none; }
.famoco-config-key {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.5;
}
.famoco-stats {
  display: flex;
  gap: 1rem;
}
.famoco-stat {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  text-align: center;
}
.famoco-stat-value {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1;
}
.famoco-stat-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.6;
  margin-top: 0.3rem;
}
</style>
